import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, Input, Switch, message, Spin } from 'antd';
import { Settings, Wallet, Landmark, Receipt, Gift, ExternalLink, ToggleRight, Megaphone, Lock, Save } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { getSystemConfig, setSystemConfig, SettingItem } from '../../../../api/admin/system.api';
import { AdminButton, AdminCard, AdminPageHeader, AdminStatusPill } from '../../ui';
import './settings.css';
import { promptTotp } from '../../../../utils/admin-stepup';

// Icon theo nhóm (nhóm do backend trả bằng tiếng Việt). Ô icon 40px: nhóm Nạp tiền màu Ca, còn lại xanh.
const GROUP_ICON: Record<string, React.ReactNode> = {
  'Nạp tiền': <Wallet size={20} />,
  'Rút tiền': <Landmark size={20} />,
  'Thuế (TNCN)': <Receipt size={20} />,
  Donate: <Gift size={20} />,
  'Bật/tắt tính năng': <ToggleRight size={20} />,
  'Chung & Thương hiệu': <Megaphone size={20} />,
};
const GROUP_TONE: Record<string, 'warning' | 'brand'> = { 'Nạp tiền': 'warning' };

// Hub gom link tới các trang cấu hình admin sẵn có.
const HUB_LINKS = [
  { to: '/admin/audit-log', label: 'Nhật ký hành động admin (audit log)' },
  { to: '/admin/gamification', label: 'Cấu hình Ca đào (máy đào, nhiệm vụ, Blind Box)' },
  { to: '/admin/cosmetic', label: 'Quản lý vật phẩm (cosmetic)' },
  { to: '/admin/donate/revenue', label: 'Báo cáo doanh thu' },
  { to: '/admin/donate/topup', label: 'Sổ nạp số dư' },
  { to: '/admin/roles', label: 'Phân quyền (RBAC)' },
];

const ManagementSettings = () => {
  const [items, setItems] = useState<SettingItem[]>([]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // resetKeys: chỉ đặt lại các key này (sau khi lưu một nhóm), giữ nguyên chỉnh sửa chưa lưu của nhóm khác.
  const load = async (resetKeys?: string[]) => {
    try {
      const data = await getSystemConfig();
      setItems(data);
      const fresh = Object.fromEntries(data.map((d) => [d.key, d.value]));
      if (resetKeys) {
        setVals((s) => ({ ...s, ...Object.fromEntries(resetKeys.filter((k) => k in fresh).map((k) => [k, fresh[k]])) }));
      } else {
        setVals(fresh);
      }
    } catch {
      message.error('Không tải được cấu hình.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Gom theo group, giữ thứ tự xuất hiện.
  const groups = useMemo(() => {
    const g: { title: string; items: SettingItem[] }[] = [];
    items.forEach((it) => {
      let grp = g.find((x) => x.title === it.group);
      if (!grp) {
        grp = { title: it.group, items: [] };
        g.push(grp);
      }
      grp.items.push(it);
    });
    return g;
  }, [items]);

  // Chia hai cột kiểu masonry: nhóm tiếp theo vào cột đang ngắn hơn (tính theo số dòng).
  const columns = useMemo(() => {
    const cols: { title: string; items: SettingItem[] }[][] = [[], []];
    const size = [0, 0];
    groups.forEach((g) => {
      const c = size[0] <= size[1] ? 0 : 1;
      cols[c].push(g);
      size[c] += g.items.length + 3;
    });
    return cols;
  }, [groups]);

  const isDirty = (it: SettingItem) => (vals[it.key] ?? it.value) !== it.value;

  const setVal = (k: string, v: string) => setVals((s) => ({ ...s, [k]: v }));

  const save = async (onlyGroup?: string) => {
    // Chỉ gửi key ĐÃ ĐỔI (để đổi banner không đụng key tiền tệ → khỏi cần TOTP).
    const changed: Record<string, string> = {};
    items.forEach((it) => {
      if (onlyGroup && it.group !== onlyGroup) return;
      const cur = vals[it.key] ?? it.value;
      if (cur !== it.value) changed[it.key] = cur;
    });
    if (Object.keys(changed).length === 0) {
      message.info('Không có thay đổi nào để lưu.');
      return;
    }

    // Đổi nhóm nhạy cảm (tiền tệ) → bắt mã Authenticator (step-up).
    const touchesSensitive = items.some((it) => it.sensitive && changed[it.key] !== undefined);
    let totp: string | undefined;
    if (touchesSensitive) {
      const code = promptTotp('đổi cấu hình tiền tệ');
      if (!code) return;
      totp = code;
    }

    setSaving(true);
    try {
      await setSystemConfig(changed, totp);
      message.success('Đã lưu cấu hình. Áp dụng ngay (không cần deploy).');
      await load(onlyGroup ? Object.keys(changed) : undefined);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Lưu thất bại.');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (it: SettingItem) => {
    const v = vals[it.key] ?? it.value;
    if (it.kind === 'bool') {
      return (
        <Switch
          checked={v === 'true'}
          onChange={(c) => setVal(it.key, c ? 'true' : 'false')}
        />
      );
    }
    if (it.kind === 'text') {
      return (
        <Input
          style={{ width: 200 }}
          aria-label={it.label}
          value={v}
          onChange={(e) => setVal(it.key, e.target.value)}
        />
      );
    }
    // int / decimal
    return (
      <div
        style={{
          width: 148,
          height: 40,
          boxSizing: 'border-box',
          border: '1px solid var(--adm-line-input)',
          borderRadius: 12,
          background: 'var(--adm-surface)',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 12px',
          flexShrink: 0,
        }}
      >
        <InputNumber
          variant="borderless"
          controls={false}
          aria-label={it.label}
          style={{ flex: 1, minWidth: 0, width: '100%' }}
          className="adm-setting-num"
          min={0}
          step={it.kind === 'decimal' ? 0.1 : 1}
          value={v === '' ? null : Number(v)}
          onChange={(n) => setVal(it.key, n == null ? '0' : String(n))}
        />
        {it.unit && <span style={{ fontSize: 12, color: 'var(--adm-muted)', whiteSpace: 'nowrap' }}>{it.unit}</span>}
      </div>
    );
  };

  if (loading)
    return (
      <div className="flex justify-center py-20">
        <Spin />
      </div>
    );

  return (
    <div className="adm-page">
      <AdminPageHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span className="adm-stat__icon adm-stat__icon--brand" aria-hidden="true">
              <Settings size={20} />
            </span>
            Cấu hình hệ thống
          </span>
        }
        description="Chỉnh quy tắc tiền tệ: nạp, rút, donate, thuế. Lưu là áp dụng ngay, không cần deploy."
        actions={
          <>
            <AdminButton variant="outline" onClick={() => load()} disabled={saving}>
              Khôi phục
            </AdminButton>
            <AdminButton elevated icon={<Save size={18} />} loading={saving} onClick={() => save()}>
              Lưu cấu hình
            </AdminButton>
          </>
        }
      />

      <div className="adm-settings-cols">
        {columns.map((col, ci) => (
          <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {col.map((g) => {
              const locked = g.items.some((it) => it.sensitive);
              return (
                <section key={g.title} className="adm-card" style={{ padding: '20px 20px 4px', gap: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, paddingBottom: 16 }}>
                    <span
                      className={`adm-stat__icon adm-stat__icon--${GROUP_TONE[g.title] ?? 'brand'}`}
                      aria-hidden="true"
                    >
                      {GROUP_ICON[g.title] ?? <Settings size={20} />}
                    </span>
                    <div style={{ flex: 1, minWidth: 0, paddingTop: locked ? 0 : 8 }}>
                      <h2 style={{ margin: 0, fontSize: 16, lineHeight: '24px', fontWeight: 600, color: 'var(--adm-heading)' }}>
                        {g.title}
                      </h2>
                      {locked && (
                        <div style={{ marginTop: 6 }}>
                          <AdminStatusPill tone="warning" icon={<Lock size={12} />}>
                            SuperAdmin · cần mã Authenticator
                          </AdminStatusPill>
                        </div>
                      )}
                    </div>
                    <AdminButton
                      variant="soft"
                      size={32}
                      icon={<Save size={14} />}
                      disabled={saving || !g.items.some(isDirty)}
                      onClick={() => save(g.title)}
                      aria-label={`Lưu nhóm ${g.title}`}
                    >
                      Lưu
                    </AdminButton>
                  </div>
                  {g.items.map((it) => (
                    <div
                      key={it.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        minHeight: 56,
                        padding: '8px 0',
                        boxSizing: 'border-box',
                        borderTop: '1px solid var(--adm-line)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, lineHeight: '20px', color: 'var(--adm-text)' }}>
                        {it.label}
                      </span>
                      {renderInput(it)}
                    </div>
                  ))}
                </section>
              );
            })}
          </div>
        ))}
      </div>

      {/* Thanh lưu cố định dưới trang: luôn với tới được khi cuộn các nhóm dài */}
      <div className="adm-card adm-settings-bar">
        <AdminButton elevated icon={<Save size={18} />} loading={saving} onClick={() => save()}>
          Lưu cấu hình
        </AdminButton>
        <AdminButton variant="outline" onClick={() => load()} disabled={saving}>
          Khôi phục
        </AdminButton>
      </div>

      {/* Hub gom link tới các trang cấu hình admin khác */}
      <AdminCard
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <span className="adm-stat__icon adm-stat__icon--brand" aria-hidden="true">
              <ExternalLink size={20} />
            </span>
            Cấu hình khác
          </span>
        }
      >
        <div className="adm-settings-hub">
          {HUB_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className="adm-settings-hub__link">
              <ExternalLink size={16} /> {l.label}
            </NavLink>
          ))}
        </div>
      </AdminCard>
    </div>
  );
};

export default ManagementSettings;
